/* System defined core library */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/* User defined library/components */
import { ExamFeatureService } from '../../../shared/services/exam-feature.service';

/* User defined models */
import { HighLightDateRange, HighLightDates } from '../../../shared/models/common/date.model';
import { DynamicFormBase } from '../../../shared/models/common/dynamic-form-base.model';
import { LpiData } from '../../../shared/models/common/lpiData.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSearch } from '../../../shared/models/scheduling/appointmentSearch.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { AppointmentSummary } from '../../../shared/models/scheduling/appointmentsummary.model';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { CardDetail } from '../../../shared/models/scheduling/carddetail.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { Eligibility, EligibilityDetail } from '../../../shared/models/scheduling/eligibility.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { LocationSelector } from '../../../shared/models/scheduling/locationSelector.model';
import { MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { Month } from '../../../shared/models/scheduling/month.model';
import { OverlapRange } from '../../../shared/models/scheduling/overlapRange.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { PaymentOption } from '../../../shared/models/scheduling/paymentOption.model';
import { CreatePaymentSessionResponse } from '../../../shared/models/scheduling/paymentSession.model';
import { Proctor } from '../../../shared/models/scheduling/proctor.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { SelectedDate } from '../../../shared/models/scheduling/selectedDate.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { SessionGroup } from '../../../shared/models/scheduling/SessionGroup.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../shared/models/scheduling/slot.model';
import { State } from '../../../shared/models/scheduling/state.model';
import { TestingAccommodation } from '../../../shared/models/scheduling/testingAccommodation.model';
import { TextReminder } from '../../../shared/models/scheduling/textReminder.model';
import { Timezone } from '../../../shared/models/scheduling/timezone.model';

/* User defined enums/constants */
import { SchedulingErrors } from '../../../../app/shared/constants/scheduling.constant';
import { DistanceUnits } from '../../../../app/shared/enums/scheduling.enum';

@Injectable()
export class SelectedExamDetailService {

  private clientSource = new BehaviorSubject<Client>(null);
  private programSource = new BehaviorSubject<Program>(null);
  private regionSource = new BehaviorSubject<State>(null);
  private examSource = new BehaviorSubject<Exam>(null);
  private siteSlotSource = new BehaviorSubject<SiteSlot>(null);
  private slotSource = new BehaviorSubject<Slot>(null);
  private countrySource = new BehaviorSubject<Country>(null);
  private lpiCountrySource = new BehaviorSubject<Country>(null);
  private lpiStateSource = new BehaviorSubject<State>(null);
  private defaultCalendarMonthSource = new BehaviorSubject<string>(null);
  private selectedStartDateSource = new BehaviorSubject<SelectedDate>(null);
  private selectedEndDateSource = new BehaviorSubject<SelectedDate>(null);
  private highLightedDateDateSource = new BehaviorSubject<HighLightDates>(null);
  private highLightedDateRangeSource = new BehaviorSubject<HighLightDateRange>(null);
  private locationSelectorSource = new BehaviorSubject<LocationSelector>(null);
  private stateSource = new BehaviorSubject<State>(null);
  private appointmentSearchSource = new BehaviorSubject<AppointmentSearch>(null);
  private bookingSource = new BehaviorSubject<Booking>(null);
  private appointmentDetailSource = new BehaviorSubject<AppointmentDetail[]>(null);
  private slotDataSource = new BehaviorSubject<AppointmentSlot>(null);
  private candidateDataSource = new BehaviorSubject<Candidate>(null);
  private appointmentSummarySource = new BehaviorSubject<AppointmentSummary>(null);
  private eligibilitySource = new BehaviorSubject<Eligibility>(null);
  private holdSource = new BehaviorSubject<Hold>(null);
  private holdsSource = new BehaviorSubject<Hold[]>(null);
  private selectedAppointmentDetailSource = new BehaviorSubject<AppointmentDetail[]>(null);
  private postedConfirmationListSource = new BehaviorSubject<string[]>(null);
  private overlapsDateSource = new BehaviorSubject<string>(null);
  private overlapsFromDateSource = new BehaviorSubject<string>(null);
  private overlapsToDateSource = new BehaviorSubject<string>(null);
  private maxEndDateLimitSource = new BehaviorSubject<string>(null);
  private textReminderSource = new BehaviorSubject<TextReminder>(null);
  private metricDetailSource = new BehaviorSubject<MetricDetails>(null);
  private metricsDetailSource = new BehaviorSubject<MetricDetails[]>(null);
  private selectedAvialableSlotsSource = new BehaviorSubject<Slot[]>(null);
  private prevSiteSlotsSource = new BehaviorSubject<SiteSlot[]>(null);
  private prevSelectionsSource = new BehaviorSubject<SiteSlot[]>(null);
  private prevSlotSelectionsSource = new BehaviorSubject<Slot[]>(null);
  private prevSitesSource = new BehaviorSubject<any[]>(null);
  private prevSelectedHoldObjSource = new BehaviorSubject<any>(null);
  private applicableTestingAccommodationSource = new BehaviorSubject<TestingAccommodation[]>(null);
  private preapprovedSelectionSource = new BehaviorSubject<string>(null);
  private preapprovedCommitSource = new BehaviorSubject<string>(null);
  private firstTimeSearchSource = new BehaviorSubject<boolean>(null);
  private firstTimeLocationSearchSource = new BehaviorSubject<boolean[]>(null);
  private skipSizeSource = new BehaviorSubject<number>(null);
  private scheduledSlotSource = new BehaviorSubject<AppointmentSlot>(null);
  private scheduledExamsSource = new BehaviorSubject<AppointmentDetail[]>(null);
  private eligibilityDetailSource = new BehaviorSubject<EligibilityDetail[]>(null);
  private programLogoSource = new BehaviorSubject<any>(null);
  private pathLockSource = new BehaviorSubject<boolean>(false);
  private lpiCandidateSource = new BehaviorSubject<Candidate>(null);
  private lpiDataSource = new BehaviorSubject<LpiData>(new LpiData());
  private candidateGuidSource = new BehaviorSubject<string>(null);
  private paymentOptionsSource = new BehaviorSubject<PaymentOption[]>(null);
  private eligFormBaseSource = new BehaviorSubject<DynamicFormBase<any>[]>(null);
  private notificationDataSource = new BehaviorSubject<any>(null);
  private orchestrationDataSource = new BehaviorSubject<any>(null);
  private countriesSource = new BehaviorSubject<Country[]>(null);
  private languageCodeSource = new BehaviorSubject<string>('en');
  private programsSource = new BehaviorSubject<Program[]>(null);
  private resourceFileCultureSource = new BehaviorSubject<string>('en-US');
  private dynamicFormBaseSource = new BehaviorSubject<DynamicFormBase<any>[]>(null);
  private formInputDataSource = new BehaviorSubject<DynamicFormBase<any>[]>(null);
  private voucherExpDateSource = new BehaviorSubject<string>(null);
  private voucherPaymentSource = new BehaviorSubject<Payment[]>(null);
  private timezoneSource = new BehaviorSubject<Timezone>(null);
  private proctorsSource = new BehaviorSubject<Proctor[]>(null);
  private voucherAttemptsSource = new BehaviorSubject<number>(0);
  private distanceUnitSource = new BehaviorSubject<DistanceUnits>(DistanceUnits.Miles);
  private timerSecondsSource = new BehaviorSubject<number>(0);
  private sessionsSource = new BehaviorSubject<Session[]>(null);
  private sessionGroupsSource = new BehaviorSubject<SessionGroup[]>(null);
  private sessionAvailabilitiesSource = new BehaviorSubject<any[]>(null);
  private voucherDetailSource = new BehaviorSubject<CardDetail>(new CardDetail());
  private discountAmountSource = new BehaviorSubject<number>(0.00);
  private batteryConfigSource = new BehaviorSubject<BatteryConfig>(null);
  private isUserNavigatingSource = new BehaviorSubject<boolean>(false);
  private skipRescheduleSource = new BehaviorSubject<boolean>(false);
  private cancelNotAllowedSource = new BehaviorSubject<boolean>(false);
  private userCaptchaInputSource = new BehaviorSubject('');
  private captchaValidationResultSource = new BehaviorSubject(false);
  private paymentOrchestratorSessionDetailSource = new BehaviorSubject<CreatePaymentSessionResponse>(null);
  private bookingRequestDataSource = new BehaviorSubject<any>(null);
  private voucherRequestDataSource = new BehaviorSubject<any>(null);
  private examDetailsForSettlementSource = new BehaviorSubject<any>(null);
  private appointmentIdsSource = new BehaviorSubject<string[]>(null);
  private examAccommodationsForNativeDeleteSource = new BehaviorSubject<any[]>(null);

  // third party Booking
  private candidateIdSource = new BehaviorSubject<string>(null);

  public outsidesClick: number = 0;
  public prevSelectedPopoverObject: any;
  public isSearchPerformed = false;
  public isRescheduleSlotSelected = false;
  public postedConfirmationNumber = "";
  public confirmationNumber = "";
  public postedErrorMessage = "";
  public showVerificationDetailView: boolean = false;
  public selectedAppointmentIndex: number = 0;
  public showReschedule = false;
  public showCancel = false;
  public doesClientExist: boolean = false;
  public programNotPublished: boolean = false;
  public isGoogleMapsValid: boolean = true;
  public sessionExpired: boolean = false;
  public timeInterval: any;
  public hasProvinces: boolean = false;
  public shouldUpdateDemographics: boolean = false;
  public proctorCountry: string = '';
  public proctorSiteId: string = '';
  public canSkipProfile: boolean = false;
  public examSetId: string = '';
  public hideExamSelection: boolean = false;
  public invalidEligibilityRequest: string = '';
  public skipMetrics: boolean = false;

  // culture/browser settings
  public culture: string = "en-US";
  public selectedLanguageCode: string = "en";
  public defaultLanguageCode: string = "en"
  public browserCulture: string = 'en-US';
  public dateFormat: string = 'MM/dd/yyyy';
  public timeFormat: boolean = false;
  public selectedLanguage: string = "English";

  // C/P/E config booleans
  public isBiometricConsentRequiredSchedule: boolean = false;
  public isBiometricConsentCheckedSchedule: boolean = false;
  public isBiometricConsentRequiredReschedule: boolean = false;
  public isBiometricConsentCheckedReschedule: boolean = false;
  public isBiometricConsentSavedInSchedule: boolean = false;
  public isProctored: boolean = false;
  public showCandidateSelectableView = false;
  public showTextReminder: boolean = false;
  public isEligibilityRequired = true;
  public isEligibilityEnabled = false;
  public isCustomAgreementRequired: boolean = false;
  public isCustomAgreementChecked: boolean = false;

  // Eligibility settings
  public isBackToBackRequired: boolean = false;
  public scheduleSetAttributeCode: boolean = false;
  public rescheduleSetAttributeCode: boolean = false;
  public cancelSetAttributeCode: boolean = false;
  public consecutiveDays: boolean = false;
  public maxDays: number;

  // Global Config settings
  public skipAppointmentLookup: boolean = false;
  public skipProfileConfig: boolean = false;
  public skipExamSelection: boolean = false;

  // lpi - when no termurl is sent and wants to skip confirmation page
  public errorOnHomePage: boolean = false;
  public programCode: string;
  public clientCode: string;
  public terminationUrl: string = "";
  public skipConfirmPage: boolean = false;

  // pathlock params
  public setSchedulePathLock = false;
  public setCancelPathLock = false;
  public setReschedulePathLock = false;
  public setReschedCancelPathLock = false;
  public setConfirmPathLock = false;
  public setSeatAvailabilityPathLock = false;
  public pathLockTestInformation: boolean = false;

  // lpi params
  public skipPrivacyPage = false;
  public lpiExamCode: string = '';
  public lpiCandidateGuid: string = "";
  public currentTargetFlow: string = "";

  // Fee
  public scheduleFee: object = {
    totalAmount: 0,
    currency: 'USD'
  };
  public rescheduleFee: object = {
    totalAmount: 0,
    currency: 'USD'
  };
  public cancellationFee: object = {
    totalAmount: 0,
    currency: 'USD'
  };
  public cancelRefund: number;
  public cancelRefundCurrency: string;
  public cancelRefundPaymentType: string;
  public cancelRefundQrTransaction: Payment;
  public isPaymentConfirmed = false;
  public isPaymentRequired: boolean = false;
  public isRefundRequired: boolean = false;
  public isVoucherFullPayment: boolean = false;
  public scheduleTransactionId: string;
  public voucherAttemptsforRedirect: number = 3;
  public voucherAttemptsforLock: number = 6;
  public voucherTransactionId: string = '';
  public customAgreementName: string = '';
  public ssoToken: string = '';
  public isCompleteApptPath: boolean = false;
  public isSessionLoadError: boolean = false;
  public isFlowComplete: boolean = false;


  client = this.clientSource.asObservable();
  program = this.programSource.asObservable();
  region = this.regionSource.asObservable();
  exam = this.examSource.asObservable();
  siteSlot = this.siteSlotSource.asObservable();
  slot = this.slotSource.asObservable();
  country = this.countrySource.asObservable();
  state = this.stateSource.asObservable();
  defaultCalendarMonth = this.defaultCalendarMonthSource.asObservable();
  selectedStartDate = this.selectedStartDateSource.asObservable();
  selectedEndDate = this.selectedEndDateSource.asObservable();
  highLightDates = this.highLightedDateDateSource.asObservable();
  locationSelector = this.locationSelectorSource.asObservable();
  appointmentSearch = this.appointmentSearchSource.asObservable();
  booking = this.bookingSource.asObservable();
  appointmentDetailData = this.appointmentDetailSource.asObservable();
  slotData = this.slotDataSource.asObservable();
  candidate = this.candidateDataSource.asObservable();
  appointmentSummary = this.appointmentSummarySource.asObservable();
  eligibility = this.eligibilitySource.asObservable();
  hold = this.holdSource.asObservable();
  holds = this.holdsSource.asObservable();
  selectedAppointmentDetails = this.selectedAppointmentDetailSource.asObservable();
  appointmentCountForInfo = new BehaviorSubject<number>(-1);
  postedConfirmationList = this.postedConfirmationListSource.asObservable();
  overlapsDate = this.overlapsDateSource.asObservable();
  textReminder = this.textReminderSource.asObservable();
  overlapsFromDate = this.overlapsFromDateSource.asObservable();
  overlapsToDate = this.overlapsToDateSource.asObservable();
  maxEndDateLimit = this.maxEndDateLimitSource.asObservable();
  metricDetails = this.metricDetailSource.asObservable();
  metricsDetail = this.metricsDetailSource.asObservable();
  selectedAvalibaleSlots = this.selectedAvialableSlotsSource.asObservable();
  prevSiteSlots = this.prevSiteSlotsSource.asObservable();
  prevSelectedHoldObj = this.prevSelectedHoldObjSource.asObservable();
  prevSelections = this.prevSelectionsSource.asObservable();
  prevSlotSelections = this.prevSlotSelectionsSource.asObservable();
  prevSites = this.prevSitesSource.asObservable();
  applicableTestingAccommodations = this.applicableTestingAccommodationSource.asObservable();
  preapprovedSelection = this.preapprovedSelectionSource.asObservable();
  preapprovedCommit = this.preapprovedCommitSource.asObservable();
  firstTimeSearch = this.firstTimeSearchSource.asObservable();
  skipSize = this.skipSizeSource.asObservable();
  firstTimeLocationSearch = this.firstTimeLocationSearchSource.asObservable();
  scheduledSlot = this.scheduledSlotSource.asObservable();
  scheduledExams = this.scheduledExamsSource.asObservable();
  eligibilityDetails = this.eligibilityDetailSource.asObservable();
  programLogo = this.programLogoSource.asObservable();
  pathLock = this.pathLockSource.asObservable();
  lpiCandidate = this.lpiCandidateSource.asObservable();
  lpiData = this.lpiDataSource.asObservable();
  lpiCountry = this.lpiCountrySource.asObservable();
  lpiState = this.lpiStateSource.asObservable();
  candidateGuid = this.candidateGuidSource.asObservable();
  paymentOptions = this.paymentOptionsSource.asObservable();
  eligFormBase = this.eligFormBaseSource.asObservable();
  notificationData = this.notificationDataSource.asObservable();
  orchestrationData = this.orchestrationDataSource.asObservable();
  countries = this.countriesSource.asObservable();
  languageCode = this.languageCodeSource.asObservable();
  programs = this.programsSource.asObservable();
  resourceFileCulture = this.resourceFileCultureSource.asObservable();
  dynamicFormBase = this.dynamicFormBaseSource.asObservable();
  formInputData = this.formInputDataSource.asObservable();
  voucherExpDate = this.voucherExpDateSource.asObservable();
  voucherPayment = this.voucherPaymentSource.asObservable();
  timezone = this.timezoneSource.asObservable();
  proctors = this.proctorsSource.asObservable();
  voucherAttempts = this.voucherAttemptsSource.asObservable();
  distanceUnit = this.distanceUnitSource.asObservable();
  sessions = this.sessionsSource.asObservable();
  sessionGroups = this.sessionGroupsSource.asObservable();
  sessionAvailabilities = this.sessionAvailabilitiesSource.asObservable();
  timerSeconds = this.timerSecondsSource.asObservable();
  voucherDetail = this.voucherDetailSource.asObservable();
  discountAmount = this.discountAmountSource.asObservable();
  batteryConfig = this.batteryConfigSource.asObservable();
  isUserNavigating = this.isUserNavigatingSource.asObservable();
  skipReschedule = this.skipRescheduleSource.asObservable();
  cancelNotAllowed = this.cancelNotAllowedSource.asObservable();
  userCaptchaInput = this.userCaptchaInputSource.asObservable();
  captchaValidationResult = this.captchaValidationResultSource.asObservable();
  paymentOrchestratorSessionDetail = this.paymentOrchestratorSessionDetailSource.asObservable();
  bookingRequestData = this.bookingRequestDataSource.asObservable();
  voucherRequestData = this.voucherRequestDataSource.asObservable();
  examDetailsForSettlement = this.examDetailsForSettlementSource.asObservable();
  appointmentIds = this.appointmentIdsSource.asObservable();
  examAccommodationsForNativeDelete = this.examAccommodationsForNativeDeleteSource.asObservable();

  // third party Booking
  candidateId = this.candidateIdSource.asObservable();

  constructor() { }

  setReminder(reminder: TextReminder) {
    this.textReminderSource.next(reminder);
  }

  setSiteSlots(siteSlots: SiteSlot[]) {
    this.prevSiteSlotsSource.next(siteSlots);
  }

  setClient(client: Client) {
    this.clientSource.next(client);
  }

  setProgram(program: Program) {
    this.programSource.next(program);
  }

  setRegion(region: State) {
    this.regionSource.next(region);
  }

  setExam(exam: Exam) {
    this.examSource.next(exam);
  }

  setMetricsDetail(metricDetail: MetricDetails[]) {
    this.metricsDetailSource.next(metricDetail);
  }

  setSiteSlot(siteSlot: SiteSlot) {
    this.siteSlotSource.next(siteSlot);
  }

  setSlot(selSlot: Slot) {
    this.slotSource.next(selSlot);
  }

  setCountry(country: Country) {
    this.countrySource.next(country);
  }

  setFirstTimeLocationSearch(firstTimeSearch: boolean[]) {
    this.firstTimeLocationSearchSource.next(firstTimeSearch);
  }

  setDefaultCalendarMonth(defaultCalendarMonth: string) {
    this.defaultCalendarMonthSource.next(defaultCalendarMonth);
  }

  setSelectedStartDate(selectedStartDate: any) {
    this.selectedStartDateSource.next(selectedStartDate);
  }

  setSelectedEndDate(selectedEndDate: any) {
    this.selectedEndDateSource.next(selectedEndDate);
  }

  setHighlightedDate(highlightedDate: any) {
    this.highLightedDateDateSource.next(highlightedDate);
  }

  setHighlightedDateRange(highlightedDateRange: any) {
    this.highLightedDateRangeSource.next(highlightedDateRange);
  }

  setSelectedLocation(locationSelector: any) {
    this.locationSelectorSource.next(locationSelector);
  }

  setState(state: any) {
    this.stateSource.next(state);
  }

  setEligibility(eligibility: Eligibility) {
    this.eligibilitySource.next(eligibility);
  }

  setAppointmentSearch(appointmentSearch: any) {
    this.appointmentSearchSource.next(appointmentSearch);
  }

  setBooking(booking: Booking) {
    if (!!booking && !!booking.tests) {
      booking.tests.sort(ExamFeatureService.compareTestForSort);
    }
    this.bookingSource.next(booking);
  }

  SetAppointmentDetail(appointmentDetails: AppointmentDetail[]) {
    if (!!appointmentDetails) {
      appointmentDetails.sort(ExamFeatureService.compareAppointmentDetailForSort);
    }
    this.appointmentDetailSource.next(appointmentDetails);
  }

  SetSlotDataSource(appointmentSlot: AppointmentSlot) {
    this.slotDataSource.next(appointmentSlot);
  }

  SetCandidateDataSource(candidate: Candidate) {
    this.candidateDataSource.next(candidate);
  }

  setAppointmentSummary(appointmentSummary: AppointmentSummary) {
    this.appointmentSummarySource.next(appointmentSummary);
  }

  setSelectedAppointmentDetails(appointmentDetails: AppointmentDetail[]) {
    if (!!appointmentDetails) {
      appointmentDetails.sort(ExamFeatureService.compareAppointmentDetailForSort);
    }
    this.selectedAppointmentDetailSource.next(appointmentDetails);
  }

  setPostedConfirmationList(postedConfirmationList: string[]) {
    this.postedConfirmationListSource.next(postedConfirmationList);
  }

  setHold(hold: Hold) {
    this.holdSource.next(hold);
  }

  setHolds(holds: Hold[]) {
    this.holdsSource.next(holds);
  }

  setOverlap(collections: AppointmentDetail[]) {
    let range: OverlapRange = ExamFeatureService.calculateOverlapDates(collections);

    this.setOverlapsDate(range.overlapsDate);

    if (!!range.overlapsFromDate) {
      this.setOverlapsFromDate(range.overlapsFromDate);
    }

    if (!!range.overlapsToDate) {
      this.setOverlapsToDate(range.overlapsToDate);
    }
  }

  setOverlapsDate(overlapsDate: string) {
    this.overlapsDateSource.next(overlapsDate);
  }

  setOverlapsFromDate(overlapsFromDate: string) {
    this.overlapsFromDateSource.next(overlapsFromDate);
  }

  setOverlapsToDate(overlapsToDate: string) {
    this.overlapsToDateSource.next(overlapsToDate);
  }

  setmaxEndDateLimit(maxEndDateLimit: string) {
    this.maxEndDateLimitSource.next(maxEndDateLimit);
  }

  setMetricDetails(metricDetail: MetricDetails) {
    this.metricDetailSource.next(metricDetail);
  }

  setSelectedAvailableSlots(slots: Slot[]) {
    this.selectedAvialableSlotsSource.next(slots);
  }

  setprevSelectedHoldObj(holdsObjArr: any) {
    this.prevSelectedHoldObjSource.next(holdsObjArr);
  }

  setPrevSelections(siteSlots: any) {
    this.prevSelectionsSource.next(siteSlots);
  }

  setPrevSlotSelections(slots: any) {
    this.prevSlotSelectionsSource.next(slots);
  }

  setPrevSites(sites: any) {
    this.prevSitesSource.next(sites);
  }

  setApplicableTestingAccommodations(testingAccommodations: TestingAccommodation[]) {
    this.applicableTestingAccommodationSource.next(testingAccommodations);
  }

  setPreapprovedSelection(selection: string) {
    this.preapprovedSelectionSource.next(selection);
  }

  setPreapprovedCommit(selection: string) {
    this.preapprovedCommitSource.next(selection);
  }

  setFirstTimeSearch(firstTimeSearch: any) {
    this.firstTimeSearchSource.next(firstTimeSearch);
  }

  setSkipSize(skipSize: number) {
    this.skipSizeSource.next(skipSize);
  }

  setScheduledSlot(slot: AppointmentSlot) {
    this.scheduledSlotSource.next(slot);
  }

  setScheduledExams(exams: AppointmentDetail[]) {
    if (!!exams) {
      exams.sort(ExamFeatureService.compareAppointmentDetailForSort);
    }
    this.scheduledExamsSource.next(exams);
  }

  setSessions(sessions: Session[]) {
    this.sessionsSource.next(sessions);
  }

  setSessionGroups(sessionGroups: SessionGroup[]) {
    this.sessionGroupsSource.next(sessionGroups);
  }

  setSessionAvailabilities(sessionAvailabilities: any[]) {
    this.sessionAvailabilitiesSource.next(sessionAvailabilities);
  }

  setEligibilityDetails(eligibilityDetails: EligibilityDetail[]) {
    this.eligibilityDetailSource.next(eligibilityDetails);
  }

  setProgramLogo(logo: any) {
    this.programLogoSource.next(logo);
  }

  setPathLock(pathLock: any) {
    this.pathLockSource.next(pathLock);
  }

  setLpiCandidateSource(candidate: Candidate) {
    this.lpiCandidateSource.next(candidate);
  }

  setLpiData(lpiData: LpiData) {
    this.lpiDataSource.next(lpiData);
  }

  setLpiCountry(country: Country) {
    this.lpiCountrySource.next(country);
  }

  setLpiState(state: State) {
    this.lpiStateSource.next(state);
  }

  setCandidateGuid(guid: string) {
    this.candidateGuidSource.next(guid);
  }

  setPaymentOptions(paymentOptions: PaymentOption[]) {
    this.paymentOptionsSource.next(paymentOptions);
  }

  setEligFormBase(eligFormBase: DynamicFormBase<any>[]) {
    this.eligFormBaseSource.next(eligFormBase);
  }

  setDynamicFormBase(dynamicFormBase: DynamicFormBase<any>[]) {
    this.dynamicFormBaseSource.next(dynamicFormBase);
  }

  setNotificationData(notificationData: any) {
    this.notificationDataSource.next(notificationData);
  }

  setOrchestrationData(orchestrationData: any) {
    this.orchestrationDataSource.next(orchestrationData);
  }

  setCountries(countries: any) {
    this.countriesSource.next(countries);
  }

  setLanguageCode(languageCode: string) {
    this.languageCodeSource.next(languageCode);
  }

  setPrograms(programs: Program[]) {
    this.programsSource.next(programs);

  }

  setResourceCulture(resourceFileCulture: string) {
    this.resourceFileCultureSource.next(resourceFileCulture);
  }

  setFormInputData(formInputData: any) {
    this.formInputDataSource.next(formInputData);
  }

  setVoucherExpDate(expirationDate: any) {
    this.voucherExpDateSource.next(expirationDate);
  }

  setVoucherPayment(payment: Payment[]) {
    this.voucherPaymentSource.next(payment);
  }

  setTimeZone(timezone: Timezone) {
    this.timezoneSource.next(timezone);
  }

  setProctors(proctors: Proctor[]) {
    this.proctorsSource.next(proctors);
  }

  incrementVoucherAttempts() {
    this.voucherAttemptsSource.next(this.voucherAttemptsSource.getValue() + 1);
  }

  setDistanceUnit(unit: DistanceUnits) {
    this.distanceUnitSource.next(unit);
  }

  setTimerSeconds(seconds: number) {
    this.timerSecondsSource.next(seconds);
  }

  setVoucherDetail(voucherDetails: CardDetail) {
    this.voucherDetailSource.next(voucherDetails);
  }

  setDiscountAmount(discount: number) {
    this.discountAmountSource.next(discount);
  }

  setBatteryConfig(batteryConfig: BatteryConfig) {
    this.batteryConfigSource.next(batteryConfig);
  }

  setSkipReschedule(skipReschedule: boolean): void {
    this.skipRescheduleSource.next(skipReschedule);
  }

  setCancelNotAllowed(cancelNotAllowed: boolean): void {
    this.cancelNotAllowedSource.next(cancelNotAllowed);
  }

  setIsUserNavigating() {
    if (!this.isUserNavigatingSource.getValue()) {
      this.isUserNavigatingSource.next(true);
      setTimeout(() => {
        this.isUserNavigatingSource.next(false);
      }, 250);
    }
  }

  setCandidateId(id: string): void {
    this.candidateIdSource.next(id)
  }

  setPaymentOrchestratorSessionDetail(paymentOrchestratorSessionDetail: CreatePaymentSessionResponse): void {
    this.paymentOrchestratorSessionDetailSource.next(paymentOrchestratorSessionDetail);
  }

  setBookingRequestData(bookingRequestData: any): void {
    this.bookingRequestDataSource.next(bookingRequestData);
  }

  setVoucherRequestData(voucherRequestData: any): void {
    this.voucherRequestDataSource.next(voucherRequestData);
  }

  setExamDetailsForSettlement(examDetailsForSettlement: any): void {
    this.examDetailsForSettlementSource.next(examDetailsForSettlement);
  }

  setAppointmentIds(appointmentIds: string[]): void {
    this.appointmentIdsSource.next(appointmentIds);
  }

  setExamAccommodationsForNativeDelete(examAccommodationsForNativeDelete: any[]): void {
    this.examAccommodationsForNativeDeleteSource.next(examAccommodationsForNativeDelete);
  }

  // Timer cleanup
  clearSlotsForTimer(): void {
    this.setSelectedAvailableSlots(null);
    this.setPrevSites([]);
    this.setPrevSlotSelections([]);
    this.setPrevSelections(null);
    this.setSlot(null);
  }

  // Resets for moving to home page
  resetExamService() {
    this.clientSource.next(null);
    this.programSource.next(null);
    this.regionSource.next(null);
    this.examSource.next(null);
    this.siteSlotSource.next(null);
    this.slotSource.next(null);
    this.countrySource.next(null);
    this.defaultCalendarMonthSource.next(null);
    this.selectedStartDateSource.next(null);
    this.selectedEndDateSource.next(null);
    this.highLightedDateDateSource.next(null);
    this.locationSelectorSource
    this.stateSource.next(null);
    this.bookingSource.next(null);
    this.appointmentDetailSource.next(null);
    this.slotDataSource.next(null);
    this.candidateDataSource.next(null);
    this.appointmentSummarySource.next(null);
    this.eligibilitySource.next(null);
    this.holdSource.next(null);
    this.holdsSource.next(null);
    this.selectedAppointmentDetailSource.next(null);
    this.appointmentCountForInfo.next(-1);
    this.overlapsDateSource.next(null);
    this.overlapsFromDateSource.next(null);
    this.overlapsToDateSource.next(null);
    this.maxEndDateLimitSource.next(null);
    this.selectedAvialableSlotsSource.next(null);
    this.prevSiteSlotsSource.next(null);
    this.prevSelectionsSource.next(null);
    this.prevSlotSelectionsSource.next(null);
    this.prevSitesSource.next(null);
    this.applicableTestingAccommodationSource.next(null);
    this.firstTimeSearchSource.next(null);
    this.skipSizeSource.next(null);
    this.firstTimeLocationSearchSource.next(null);
    this.scheduledSlotSource.next(null);
    this.eligibilityDetailSource.next(null);
    this.programLogoSource.next(null);
    this.pathLockSource.next(null);
    this.lpiCountrySource.next(null);
    this.lpiStateSource.next(null);
    this.candidateGuidSource.next(null);
    this.eligFormBaseSource.next(null);
    this.orchestrationDataSource.next(null);
    this.notificationDataSource.next(null);
    this.paymentOptionsSource.next(null);
    this.countriesSource.next(null);
    this.programsSource.next(null);
    this.dynamicFormBaseSource.next(null);
    this.formInputDataSource.next(null);
    this.voucherExpDateSource.next(null);
    this.voucherPaymentSource.next(null);
    this.timezoneSource.next(null);
    this.proctorsSource.next(null);
    this.metricDetailSource.next(null);
    this.metricsDetailSource.next(null);
    this.sessionsSource.next(null);
    this.sessionGroupsSource.next(null);
    this.sessionAvailabilitiesSource.next(null);
    this.timerSecondsSource.next(0);
    this.voucherDetailSource.next(new CardDetail());
    this.discountAmountSource.next(0.00);
    this.batteryConfigSource.next(null);
    this.isUserNavigatingSource.next(null);
    this.skipRescheduleSource.next(null);
    this.cancelNotAllowedSource.next(null);
    this.userCaptchaInputSource.next(null);
    this.captchaValidationResultSource.next(null);
    this.paymentOrchestratorSessionDetailSource.next(null);
    this.bookingRequestDataSource.next(null);
    this.voucherRequestDataSource.next(null);
    this.examDetailsForSettlementSource.next(null);
    this.appointmentIdsSource.next(null);
    this.examAccommodationsForNativeDeleteSource.next(null);

    this.isPaymentConfirmed = false;
    this.isRescheduleSlotSelected = false;
    this.showReschedule = false;
    this.showCancel = false;
    this.postedConfirmationNumber = '';
    this.confirmationNumber = '';
    this.voucherTransactionId = '';

    this.scheduleFee = {
      totalAmount: 0,
      currency: 'USD'
    };
    this.rescheduleFee = {
      totalAmount: 0,
      currency: 'USD'
    };
    this.cancellationFee = {
      totalAmount: 0,
      currency: 'USD'
    };

    this.currentTargetFlow = '';
    this.isPaymentRequired = false;
    this.isRefundRequired = false;
    this.showCandidateSelectableView = false;
    this.doesClientExist = false;
    this.selectedAppointmentIndex = 0;
    this.isCompleteApptPath = false;
    this.isSessionLoadError = false;
    this.isFlowComplete = false;
  }

  // returns the captcha text entered by user
  public getUserCaptchaInput(): Observable<string> {
    return this.userCaptchaInput;
  }

  // set captcha text entered by user
  public setUserCaptchInput(captcha: string) {
    this.userCaptchaInputSource.next(captcha);
  }

  // returns the validation result of captcha entered by user
  public getCaptchaValidationResult(): Observable<boolean> {
    return this.captchaValidationResult;
  }

  // set the validation result of captcha
  public setCaptchaValidationResult(isValid: boolean) {
    this.captchaValidationResultSource.next(isValid);
  }

  saveToLocalStorage(sessionId: string) {
    const data = {
      sessionId: sessionId,

      client: this.clientSource.value,
      program: this.programSource.value,
      region: this.regionSource.value,
      exam: this.examSource.value,
      siteSlot: this.siteSlotSource.value,
      slot: this.slotSource.value,
      country: this.countrySource.value,
      lpiCountry: this.lpiCountrySource.value,
      lpiState: this.lpiStateSource.value,
      defaultCalendarMonth: this.defaultCalendarMonthSource.value,
      selectedStartDate: this.selectedStartDateSource.value,
      selectedEndDate: this.selectedEndDateSource.value,
      highLightDates: this.highLightedDateDateSource.value,
      highLightDateRange: this.highLightedDateRangeSource.value,
      locationSelector: this.locationSelectorSource.value,
      state: this.stateSource.value,
      appointmentSearch: this.appointmentSearchSource.value,
      booking: this.bookingSource.value,
      appointmentDetail: this.appointmentDetailSource.value,
      slotData: this.slotDataSource.value,
      candidate: this.candidateDataSource.value,
      appointmentSummary: this.appointmentSummarySource.value,
      eligibility: this.eligibilitySource.value,
      hold: this.holdSource.value,
      holds: this.holdsSource.value,
      selectedAppointmentDetails: this.selectedAppointmentDetailSource.value,
      appointmentCountForInfo: this.appointmentCountForInfo.value,
      postedConfirmationList: this.postedConfirmationListSource.value,
      overlapsDate: this.overlapsDateSource.value,
      textReminder: this.textReminderSource.value,
      overlapsFromDate: this.overlapsFromDateSource.value,
      overlapsToDate: this.overlapsToDateSource.value,
      maxEndDateLimit: this.maxEndDateLimitSource.value,
      metricDetails: this.metricDetailSource.value,
      metricsDetail: this.metricsDetailSource.value,
      selectedAvalibaleSlots: this.selectedAvialableSlotsSource.value,
      prevSiteSlots: this.prevSiteSlotsSource.value,
      prevSelectedHoldObj: this.prevSelectedHoldObjSource.value,
      prevSelections: this.prevSelectionsSource.value,
      prevSlotSelections: this.prevSlotSelectionsSource.value,
      prevSites: this.prevSitesSource.value,
      applicableTestingAccommodations: this.applicableTestingAccommodationSource.value,
      preapprovedSelection: this.preapprovedSelectionSource.value,
      preapprovedCommit: this.preapprovedCommitSource.value,
      firstTimeSearch: this.firstTimeSearchSource.value,
      skipSize: this.skipSizeSource.value,
      firstTimeLocationSearch: this.firstTimeLocationSearchSource.value,
      scheduledSlot: this.scheduledSlotSource.value,
      scheduledExams: this.scheduledExamsSource.value,
      eligibilityDetails: this.eligibilityDetailSource.value,
      programLogo: this.programLogoSource.value,
      pathLock: this.pathLockSource.value,
      lpiCandidate: this.lpiCandidateSource.value,
      lpiData: this.lpiDataSource.value,
      candidateGuid: this.candidateGuidSource.value,
      paymentOptions: this.paymentOptionsSource.value,
      // eligFormBase: this.eligFormBaseSource.value,
      notificationData: this.notificationDataSource.value,
      orchestrationData: this.orchestrationDataSource.value,
      countries: this.countriesSource.value,
      languageCode: this.languageCodeSource.value,
      programs: this.programsSource.value,
      resourceFileCulture: this.resourceFileCultureSource.value,
      // dynamicFormBase: this.dynamicFormBaseSource.value,
      // formInputData: this.formInputDataSource.value,
      voucherExpDate: this.voucherExpDateSource.value,
      voucherPayment: this.voucherPaymentSource.value,
      timezone: this.timezoneSource.value,
      proctors: this.proctorsSource.value,
      voucherAttempts: this.voucherAttemptsSource.value,
      distanceUnit: this.distanceUnitSource.value,
      sessions: this.sessionsSource.value,
      sessionGroups: this.sessionGroupsSource.value,
      sessionAvailabilities: this.sessionAvailabilitiesSource.value,
      timerSeconds: this.timerSecondsSource.value,
      voucherDetail: this.voucherDetailSource.value,
      discountAmount: this.discountAmountSource.value,
      batteryConfig: this.batteryConfigSource.value,
      isUserNavigating: this.isUserNavigatingSource.value,
      skipReschedule: this.skipRescheduleSource.value,
      cancelNotAllowed: this.cancelNotAllowedSource.value,
      userCaptchaInput: this.userCaptchaInputSource.value,
      captchaValidationResult: this.captchaValidationResultSource.value,
      paymentOrchestratorSessionDetail: this.paymentOrchestratorSessionDetailSource.value,
      bookingRequestData: this.bookingRequestDataSource.value,
      voucherRequestData: this.voucherRequestDataSource.value,
      examDetailsForSettlement: this.examDetailsForSettlementSource.value,
      appointmentIds: this.appointmentIdsSource.value,
      examAccommodationsForNativeDelete: this.examAccommodationsForNativeDeleteSource.value,
      candidateId: this.candidateIdSource.value,

      outsidesClick: this.outsidesClick,
      prevSelectedPopoverObject: this.prevSelectedPopoverObject,
      isSearchPerformed: this.isSearchPerformed,
      isRescheduleSlotSelected: this.isRescheduleSlotSelected,
      postedConfirmationNumber: this.postedConfirmationNumber,
      confirmationNumber: this.confirmationNumber,
      postedErrorMessage: this.postedErrorMessage,
      showVerificationDetailView: this.showVerificationDetailView,
      selectedAppointmentIndex: this.selectedAppointmentIndex,
      showReschedule: this.showReschedule,
      showCancel: this.showCancel,
      doesClientExist: this.doesClientExist,
      programNotPublished: this.programNotPublished,
      isGoogleMapsValid: this.isGoogleMapsValid,
      sessionExpired: this.sessionExpired,
      timeInterval: this.timeInterval,
      hasProvinces: this.hasProvinces,
      shouldUpdateDemographics: this.shouldUpdateDemographics,
      proctorCountry: this.proctorCountry,
      proctorSiteId: this.proctorSiteId,
      canSkipProfile: this.canSkipProfile,
      examSetId: this.examSetId,
      hideExamSelection: this.hideExamSelection,
      invalidEligibilityRequest: this.invalidEligibilityRequest,
      culture: this.culture,
      selectedLanguageCode: this.selectedLanguageCode,
      defaultLanguageCode: this.defaultLanguageCode,
      browserCulture: this.browserCulture,
      dateFormat: this.dateFormat,
      timeFormat: this.timeFormat,
      selectedLanguage: this.selectedLanguage,
      isBiometricConsentRequiredSchedule: this.isBiometricConsentRequiredSchedule,
      isBiometricConsentCheckedSchedule: this.isBiometricConsentCheckedSchedule,
      isBiometricConsentRequiredReschedule: this.isBiometricConsentRequiredReschedule,
      isBiometricConsentCheckedReschedule: this.isBiometricConsentCheckedReschedule,
      isBiometricConsentSavedInSchedule: this.isBiometricConsentSavedInSchedule,
      isProctored: this.isProctored,
      showCandidateSelectableView: this.showCandidateSelectableView,
      showTextReminder: this.showTextReminder,
      isEligibilityRequired: this.isEligibilityRequired,
      isEligibilityEnabled: this.isEligibilityEnabled,
      isCustomAgreementRequired: this.isCustomAgreementRequired,
      isCustomAgreementChecked: this.isCustomAgreementChecked,
      isBackToBackRequired: this.isBackToBackRequired,
      scheduleSetAttributeCode: this.scheduleSetAttributeCode,
      rescheduleSetAttributeCode: this.rescheduleSetAttributeCode,
      cancelSetAttributeCode: this.cancelSetAttributeCode,
      consecutiveDays: this.consecutiveDays,
      maxDays: this.maxDays,
      skipAppointmentLookup: this.skipAppointmentLookup,
      skipProfileConfig: this.skipProfileConfig,
      skipExamSelection: this.skipExamSelection,
      errorOnHomePage: this.errorOnHomePage,
      programCode: this.programCode,
      clientCode: this.clientCode,
      terminationUrl: this.terminationUrl,
      skipConfirmPage: this.skipConfirmPage,
      setSchedulePathLock: this.setSchedulePathLock,
      setCancelPathLock: this.setCancelPathLock,
      setReschedulePathLock: this.setReschedulePathLock,
      setReschedCancelPathLock: this.setReschedCancelPathLock,
      setConfirmPathLock: this.setConfirmPathLock,
      setSeatAvailabilityPathLock: this.setSeatAvailabilityPathLock,
      pathLockTestInformation: this.pathLockTestInformation,
      skipPrivacyPage: this.skipPrivacyPage,
      lpiExamCode: this.lpiExamCode,
      lpiCandidateGuid: this.lpiCandidateGuid,
      currentTargetFlow: this.currentTargetFlow,
      scheduleFee: this.scheduleFee,
      rescheduleFee: this.rescheduleFee,
      cancellationFee: this.cancellationFee,
      cancelRefund: this.cancelRefund,
      cancelRefundCurrency: this.cancelRefundCurrency,
      cancelRefundPaymentType: this.cancelRefundPaymentType,
      cancelRefundQrTransaction: this.cancelRefundQrTransaction,
      isPaymentConfirmed: this.isPaymentConfirmed,
      isPaymentRequired: this.isPaymentRequired,
      isRefundRequired: this.isRefundRequired,
      isVoucherFullPayment: this.isVoucherFullPayment,
      scheduleTransactionId: this.scheduleTransactionId,
      voucherAttemptsforRedirect: this.voucherAttemptsforRedirect,
      voucherAttemptsforLock: this.voucherAttemptsforLock,
      voucherTransactionId: this.voucherTransactionId,
      customAgreementName: this.customAgreementName,
      ssoToken: this.ssoToken,
      skipMetrics: this.skipMetrics
    };

    try {
      window.localStorage.setItem(sessionId, JSON.stringify(data));
      return;
    } catch (e) {
      return {
        message: {
          id: SchedulingErrors.SetStorageError,
          message: 'errors.error',
          description: 'errors.thereWasAnIssue'
        },
        error: e
      }
    }
  }

  loadFromLocalStorage(key: string) {
    try {
      const data = JSON.parse(window.localStorage.getItem(key));

      if (!!data) {
        const sessionId = data.sessionId;

        this.clientSource.next(data.client);
        this.programSource.next(data.program);
        this.regionSource.next(data.region);
        this.examSource.next(data.exam);
        this.siteSlotSource.next(data.siteSlot);
        this.slotSource.next(data.slot);
        this.countrySource.next(data.country);
        this.lpiCountrySource.next(data.lpiCountry);
        this.lpiStateSource.next(data.lpiState);
        this.defaultCalendarMonthSource.next(data.defaultCalendarMonth);
        this.selectedStartDateSource.next(data.selectedStartDate);
        this.selectedEndDateSource.next(data.selectedEndDate);
        this.highLightedDateDateSource.next(data.highLightDates);
        this.highLightedDateRangeSource.next(data.highLightDateRange);
        this.locationSelectorSource.next(data.locationSelector);
        this.stateSource.next(data.state);
        this.appointmentSearchSource.next(data.appointmentSearch);
        this.bookingSource.next(data.booking)
        this.appointmentDetailSource.next(data.appointmentDetail);
        this.slotDataSource.next(data.slotData);
        this.candidateDataSource.next(data.candidate);
        this.appointmentSummarySource.next(data.appointmentSummary);
        this.eligibilitySource.next(data.eligibility);
        this.holdSource.next(data.hold);
        this.holdsSource.next(data.holds);
        this.selectedAppointmentDetailSource.next(data.selectedAppointmentDetails);
        this.appointmentCountForInfo.next(data.appointmentCountForInfo);
        this.postedConfirmationListSource.next(data.postedConfirmationList);
        this.overlapsDateSource.next(data.overlapsDate);
        this.textReminderSource.next(data.textReminder);
        this.overlapsFromDateSource.next(data.overlapsFromDate);
        this.overlapsToDateSource.next(data.overlapsToDate);
        this.maxEndDateLimitSource.next(data.maxEndDateLimit);
        this.metricDetailSource.next(data.metricDetails);
        this.metricsDetailSource.next(data.metricsDetail);
        this.selectedAvialableSlotsSource.next(data.selectedAvalibaleSlots);
        this.prevSiteSlotsSource.next(data.prevSiteSlots);
        this.prevSelectedHoldObjSource.next(data.prevSelectedHoldObj);
        this.prevSelectionsSource.next(data.prevSelections);
        this.prevSlotSelectionsSource.next(data.prevSlotSelections);
        this.prevSitesSource.next(data.prevSites);
        this.applicableTestingAccommodationSource.next(data.applicableTestingAccommodations);
        this.preapprovedSelectionSource.next(data.preapprovedSelection);
        this.preapprovedCommitSource.next(data.preapprovedCommit);
        this.firstTimeSearchSource.next(data.firstTimeSearch);
        this.skipSizeSource.next(data.skipSize);
        this.firstTimeLocationSearchSource.next(data.firstTimeLocationSearch);
        this.scheduledSlotSource.next(data.scheduledSlot);
        this.scheduledExamsSource.next(data.scheduledExams);
        this.eligibilityDetailSource.next(data.eligibilityDetails);
        this.programLogoSource.next(data.programLogo);
        this.pathLockSource.next(data.pathLock);
        this.lpiCandidateSource.next(data.lpiCandidate);
        this.lpiDataSource.next(data.lpiData);
        this.candidateGuidSource.next(data.candidateGuid);
        this.paymentOptionsSource.next(data.paymentOptions);
        // this.eligFormBaseSource.next(data.eligFormBase);
        this.notificationDataSource.next(data.notificationData);
        this.orchestrationDataSource.next(data.orchestrationData);
        this.countriesSource.next(data.countries);
        this.languageCodeSource.next(data.languageCode);
        this.programsSource.next(data.programs);
        this.resourceFileCultureSource.next(data.resourceFileCulture);
        // this.dynamicFormBaseSource.next(data.dynamicFormBase);
        // this.formInputDataSource.next(data.formInputData);
        this.voucherExpDateSource.next(data.voucherExpDate);
        this.voucherPaymentSource.next(data.voucherPayment);
        this.timezoneSource.next(data.timezone);
        this.proctorsSource.next(data.proctors);
        this.voucherAttemptsSource.next(data.voucherAttempts);
        this.distanceUnitSource.next(data.distanceUnit);
        this.sessionsSource.next(data.sessions);
        this.sessionGroupsSource.next(data.sessionGroups);
        this.sessionAvailabilitiesSource.next(data.sessionAvailabilities);
        this.timerSecondsSource.next(data.timerSeconds);
        this.voucherDetailSource.next(data.voucherDetail);
        this.discountAmountSource.next(data.discountAmount);
        this.batteryConfigSource.next(data.batteryConfig);
        this.isUserNavigatingSource.next(data.isUserNavigating);
        this.skipRescheduleSource.next(data.skipReschedule);
        this.cancelNotAllowedSource.next(data.cancelNotAllowed);
        this.userCaptchaInputSource.next(data.userCaptchaInput);
        this.captchaValidationResultSource.next(data.captchaValidationResult);
        this.paymentOrchestratorSessionDetailSource.next(data.paymentOrchestratorSessionDetail);
        this.bookingRequestDataSource.next(data.bookingRequestData);
        this.voucherRequestDataSource.next(data.voucherRequestData);
        this.examDetailsForSettlementSource.next(data.examDetailsForSettlement);
        this.appointmentIdsSource.next(data.appointmentIds);
        this.examAccommodationsForNativeDeleteSource.next(data.examAccommodationsForNativeDelete);
        this.candidateIdSource.next(data.candidateId);

        this.outsidesClick = data.outsidesClick;
        this.prevSelectedPopoverObject = data.prevSelectedPopoverObject;
        this.isSearchPerformed = data.isSearchPerformed;
        this.isRescheduleSlotSelected = data.isRescheduleSlotSelected;
        this.postedConfirmationNumber = data.postedConfirmationNumber;
        this.confirmationNumber = data.confirmationNumber;
        this.postedErrorMessage = data.postedErrorMessage;
        this.showVerificationDetailView = data.showVerificationDetailView;
        this.selectedAppointmentIndex = data.selectedAppointmentIndex;
        this.showReschedule = data.showReschedule;
        this.showCancel = data.showCancel;
        this.doesClientExist = data.doesClientExist;
        this.programNotPublished = data.programNotPublished;
        this.isGoogleMapsValid = data.isGoogleMapsValid;
        this.sessionExpired = data.sessionExpired;
        this.timeInterval = data.timeInterval;
        this.hasProvinces = data.hasProvinces;
        this.shouldUpdateDemographics = data.shouldUpdateDemographics;
        this.proctorCountry = data.proctorCountry;
        this.proctorSiteId = data.proctorSiteId;
        this.canSkipProfile = data.canSkipProfile;
        this.examSetId = data.examSetId;
        this.hideExamSelection = data.hideExamSelection;
        this.invalidEligibilityRequest = data.invalidEligibilityRequest;
        this.culture = data.culture;
        this.selectedLanguageCode = data.selectedLanguageCode;
        this.defaultLanguageCode = data.defaultLanguageCode;
        this.browserCulture = data.browserCulture;
        this.dateFormat = data.dateFormat;
        this.timeFormat = data.timeFormat;
        this.selectedLanguage = data.selectedLanguage;
        this.isBiometricConsentRequiredSchedule = data.isBiometricConsentRequiredSchedule;
        this.isBiometricConsentCheckedSchedule = data.isBiometricConsentCheckedSchedule;
        this.isBiometricConsentRequiredReschedule = data.isBiometricConsentRequiredReschedule;
        this.isBiometricConsentCheckedReschedule = data.isBiometricConsentCheckedReschedule;
        this.isBiometricConsentSavedInSchedule = data.isBiometricConsentSavedInSchedule;
        this.isProctored = data.isProctored;
        this.showCandidateSelectableView = data.showCandidateSelectableView;
        this.showTextReminder = data.showTextReminder;
        this.isEligibilityRequired = data.isEligibilityRequired;
        this.isEligibilityEnabled = data.isEligibilityEnabled;
        this.isCustomAgreementRequired = data.isCustomAgreementRequired;
        this.isCustomAgreementChecked = data.isCustomAgreementChecked;
        this.isBackToBackRequired = data.isBackToBackRequired;
        this.scheduleSetAttributeCode = data.scheduleSetAttributeCode;
        this.rescheduleSetAttributeCode = data.rescheduleSetAttributeCode;
        this.cancelSetAttributeCode = data.cancelSetAttributeCode;
        this.consecutiveDays = data.consecutiveDays;
        this.maxDays = data.maxDays;
        this.skipAppointmentLookup = data.skipAppointmentLookupskipAppointmentLookup;
        this.skipProfileConfig = data.skipProfileConfig;
        this.skipExamSelection = data.skipExamSelection;
        this.errorOnHomePage = data.errorOnHomePage;
        this.programCode = data.programCode;
        this.clientCode = data.clientCode;
        this.terminationUrl = data.terminationUrl;
        this.skipConfirmPage = data.skipConfirmPage;
        this.setSchedulePathLock = data.setSchedulePathLock;
        this.setCancelPathLock = data.setCancelPathLock;
        this.setReschedulePathLock = data.setReschedulePathLock;
        this.setReschedCancelPathLock = data.setReschedCancelPathLock;
        this.setConfirmPathLock = data.setConfirmPathLock;
        this.setSeatAvailabilityPathLock = data.setSeatAvailabilityPathLock;
        this.pathLockTestInformation = data.pathLockTestInformation;
        this.skipPrivacyPage = data.skipPrivacyPage;
        this.lpiExamCode = data.lpiExamCode;
        this.lpiCandidateGuid = data.lpiCandidateGuid;
        this.currentTargetFlow = data.currentTargetFlow;
        this.scheduleFee = data.scheduleFee;
        this.rescheduleFee = data.rescheduleFee;
        this.cancellationFee = data.cancellationFee;
        this.cancelRefund = data.cancelRefund;
        this.cancelRefundCurrency = data.cancelRefundCurrency;
        this.cancelRefundPaymentType = data.cancelRefundPaymentType;
        this.cancelRefundQrTransaction = data.cancelRefundQrTransaction;
        this.isPaymentConfirmed = data.isPaymentConfirmed;
        this.isPaymentRequired = data.isPaymentRequired;
        this.isRefundRequired = data.isRefundRequired;
        this.isVoucherFullPayment = data.isVoucherFullPayment;
        this.scheduleTransactionId = data.scheduleTransactionId;
        this.voucherAttemptsforRedirect = data.voucherAttemptsforRedirect;
        this.voucherAttemptsforLock = data.voucherAttemptsforLock;
        this.voucherTransactionId = data.voucherTransactionId;
        this.customAgreementName = data.customAgreementName;
        this.ssoToken = data.ssoToken;
        this.skipMetrics = data.skipMetrics;

        return { sessionId };
      } else {
        // local storage is accessible but missing
        return { error: `Session data not found in local storage for: ${key}` };
      }

    } catch (e) {
      // local storage inaccessible
      return {
        error: `Error in retrieving data from local storage for: ${key}`,
        exception: e
      };
    }
  }
}
